import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import {
  Loader2, Sparkles, Shield, Fingerprint, Copy, Check,
  Facebook, MessageCircle, Twitter, ExternalLink, Eye, Calendar,
} from "lucide-react";

/**
 * PublicComparison, fully anonymous public page for a tenant-shared
 * photo comparison. NO tenant name/address/agent name shown. Just the
 * state, photos (if enabled), AI summary (if enabled), context (if
 * enabled), SHA-256 fingerprints and a strong CTA to TenantTrail.
 *
 * Designed to look great when pasted into a Facebook tenancy group:
 * Open Graph tags + watermarked composite + verified fingerprints.
 */
export default function PublicComparison() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/comparisons/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "This comparison is no longer shared."))
      .finally(() => setLoading(false));
  }, [token]);

  // Update OG tags + title for social previews
  useEffect(() => {
    if (!data) return;
    const title = `${data.title} · ${data.anonymous_byline} · TenantTrail`;
    const desc = data.ai_summary ? data.ai_summary.slice(0, 200) + "…" : "A verified, anonymous tenant photo comparison from TenantTrail, the rental co-pilot for Australian renters.";
    document.title = title;
    const setMeta = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = sel.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k, v.replaceAll('"', ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:url"]', "content", pageUrl);
    if (data.share_show_photos && data.composite_url) {
      setMeta('meta[property="og:image"]', "content", data.composite_url);
      setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
      setMeta('meta[name="twitter:image"]', "content", data.composite_url);
    }
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
  }, [data, pageUrl]);

  const copyLink = () => {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F4]">
        <div className="flex items-center gap-2 text-[#5a6b7a] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading comparison…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F4] p-6">
        <div className="card max-w-md text-center">
          <Shield size={28} strokeWidth={1.5} className="text-[#5a6b7a] mx-auto mb-3" />
          <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Not available</h2>
          <p className="text-sm text-[#5a6b7a] mb-4">{error}</p>
          <Link to="/" className="btn-teal text-sm inline-flex items-center gap-1.5">
            Visit TenantTrail <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    );
  }

  const shareText = encodeURIComponent(`${data.anonymous_byline}: ${data.title}, verified photo comparison via TenantTrail`);
  const shareUrl = encodeURIComponent(pageUrl);

  return (
    <div className="min-h-screen bg-[#FAF9F4]" data-testid="public-comparison">
      {/* Top brand bar */}
      <header className="bg-[#0D2B52] text-white py-3 px-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-90">
            <Shield size={18} strokeWidth={1.5} />
            <span className="font-serif text-lg">TenantTrail</span>
          </Link>
          <Link to="/" className="text-xs font-mono opacity-75 hover:opacity-100 hidden sm:flex items-center gap-1">
            tenanttrail.net <ExternalLink size={10} />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Anonymous byline */}
        <div className="mb-2">
          <span className="inline-block px-2.5 py-0.5 bg-[#E6F5F2] text-[#0D2B52] text-[11px] font-mono uppercase tracking-wide" data-testid="public-byline">
            <Eye size={10} className="inline -mt-0.5 mr-1" />
            Anonymous · {data.anonymous_byline}
          </span>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-2 leading-tight" data-testid="public-title">
          {data.title}
        </h1>
        <div className="text-xs text-[#5a6b7a] font-mono mb-6 flex flex-wrap items-center gap-3">
          {data.created_at && (
            <span><Calendar size={10} className="inline -mt-0.5 mr-1" />Shared {new Date(data.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</span>
          )}
          <span>· {data.view_count + 1} {(data.view_count + 1) === 1 ? "view" : "views"}</span>
        </div>

        {/* Composite image */}
        {data.share_show_photos && data.composite_url && (
          <div className="mb-8" data-testid="public-composite-wrap">
            <a href={data.composite_url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-[#E5E4DE] shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <img src={data.composite_url} alt="Photo comparison" className="w-full h-auto" data-testid="public-composite-img" />
            </a>
            <p className="text-[11px] text-[#6B7280] font-mono italic mt-1.5">
              The watermark and verification footer are baked into the image, it travels with screenshots.
            </p>
          </div>
        )}

        {/* AI summary */}
        {data.share_show_summary && data.ai_summary && (
          <section className="mb-8 bg-[#FAF6E8] border border-[#C9A961]/40 p-5 sm:p-6" data-testid="public-summary">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#8A6D3B]" />
              <span className="label-mono text-[10px] text-[#8A6D3B]">Compass&apos;s plain-English read</span>
            </div>
            <div className="font-serif text-[15px] sm:text-[16px] leading-relaxed text-[#1A1A1A] whitespace-pre-wrap">
              {data.ai_summary}
            </div>
          </section>
        )}

        {/* Tenant's context */}
        {data.share_show_context && data.user_context && (
          <section className="mb-8 border-l-4 border-l-[#1B6C62] bg-white p-5 sm:p-6" data-testid="public-context">
            <div className="label-mono text-[10px] text-[#1B6C62] mb-2">In the tenant&apos;s own words</div>
            <p className="font-serif text-[15px] leading-relaxed text-[#1A1A1A] italic">&ldquo;{data.user_context}&rdquo;</p>
          </section>
        )}

        {/* Fingerprints, tribunal verification */}
        <section className="mb-10 bg-white border border-[#E5E4DE] p-4 sm:p-5" data-testid="public-fingerprints">
          <div className="flex items-center gap-2 mb-3">
            <Fingerprint size={13} strokeWidth={1.5} className="text-[#5a6b7a]" />
            <span className="label-mono text-[10px] text-[#5a6b7a]">Tamper-evident verification</span>
          </div>
          <p className="text-[12px] text-[#5a6b7a] mb-3">
            Each source photo has a SHA-256 fingerprint baked in. If either photo is edited after this comparison was created, the fingerprint will not match, an agent or tribunal can verify integrity by re-hashing the file.
          </p>
          <dl className="grid sm:grid-cols-2 gap-3 text-[11px] font-mono">
            <div>
              <dt className="text-[#5a6b7a] uppercase tracking-wide mb-1">Tenant photo{data.user_photo_date ? ` · ${data.user_photo_date}` : ""}</dt>
              <dd className="break-all bg-[#F4F3ED] p-2 border border-[#E5E4DE]" data-testid="public-user-sha">{data.user_sha256 || ","}</dd>
            </div>
            <div>
              <dt className="text-[#5a6b7a] uppercase tracking-wide mb-1">Agent photo{data.agent_photo_date ? ` · ${data.agent_photo_date}` : ""}</dt>
              <dd className="break-all bg-[#F4F3ED] p-2 border border-[#E5E4DE]" data-testid="public-agent-sha">{data.agent_sha256 || ","}</dd>
            </div>
          </dl>
        </section>

        {/* Share buttons */}
        <section className="mb-10" data-testid="public-share-bar">
          <div className="label-mono text-[10px] text-[#5a6b7a] mb-2">Share this comparison</div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1877F2] text-white text-sm hover:opacity-90"
              data-testid="public-share-facebook"
            ><Facebook size={14} strokeWidth={1.75} /> Facebook</a>
            <a
              href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-black text-white text-sm hover:opacity-90"
              data-testid="public-share-x"
            ><Twitter size={14} strokeWidth={1.75} /> X</a>
            <a
              href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] text-white text-sm hover:opacity-90"
              data-testid="public-share-whatsapp"
            ><MessageCircle size={14} strokeWidth={1.75} /> WhatsApp</a>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E4DE] text-[#0D2B52] text-sm hover:bg-[#F4F3ED]"
              data-testid="public-share-copy"
            >
              {copied ? (<><Check size={14} strokeWidth={2} /> Copied</>) : (<><Copy size={14} strokeWidth={1.75} /> Copy link</>)}
            </button>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-[#0D2B52] to-[#1B6C62] text-white p-6 sm:p-8" data-testid="public-cta">
          <h2 className="font-serif text-2xl sm:text-3xl mb-2 leading-tight">TenantTrail, your rental co-pilot.</h2>
          <p className="text-[15px] opacity-90 mb-5 leading-relaxed max-w-xl">
            Track repairs, log evidence and draft polished letters. Keep everyone on the same page, calmly, clearly and properly documented. Free to start.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-1.5 bg-white text-[#0D2B52] px-5 py-2.5 text-sm font-medium hover:bg-[#FAF9F4] transition-colors"
              data-testid="public-cta-btn"
            >
              Start free <ExternalLink size={13} strokeWidth={1.75} />
            </Link>
            <Link
              to="/features"
              className="text-sm text-white/85 hover:text-white underline-offset-4 hover:underline"
              data-testid="public-cta-features"
            >
              See what TenantTrail can do →
            </Link>
          </div>
        </section>

        <footer className="mt-8 text-center text-[11px] text-[#5a6b7a] font-mono">
          Comparison ID {data.short_id} · TenantTrail · Anonymous by design, no tenant name, address, or agent name is ever published.
        </footer>
      </main>
    </div>
  );
}
